import { format } from 'date-fns';
import { ClosureType } from '@/types';

export interface ShiftData {
  id: string;
  employee_id: string | null;
  start_time: string;
  end_time: string;
}

export interface OpeningHoursData {
  id: string;
  day_of_week: string;
  opening_time: string;
  closing_time: string;
  is_closed_all_day: boolean;
  is_24_hours?: boolean;
}

export type ValidationStatus = 'red' | 'orange' | 'green' | 'closed';

export interface DayValidationResult {
  status: ValidationStatus;
  hasShifts: boolean;
  allShiftsAssigned: boolean;
  shiftsCount: number;
  validationMessage?: string;
  openingHoursCovered?: boolean;
  coverageGaps?: Array<{ start: string; end: string; durationMinutes: number }>;
  closureType?: ClosureType | null;
}

/**
 * Check if assigned shifts provide complete coverage of opening hours
 */
export const checkOpeningHoursCoverage = (
  assignedShifts: ShiftData[],
  openingHours: OpeningHoursData | null
): { isCovered: boolean; gaps: Array<{ start: string; end: string; durationMinutes: number }> } => {
  // If no opening hours data or business is closed all day, no coverage required
  if (!openingHours || openingHours.is_closed_all_day) {
    return { isCovered: true, gaps: [] };
  }

  // If business is 24 hours, check for continuous coverage
  if (openingHours.is_24_hours) {
    // Sort shifts by start time
    const sortedShifts = assignedShifts.sort((a, b) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    // Check for 24-hour continuous coverage
    // This is complex, so for now we'll be lenient and return true if there are shifts
    return {
      isCovered: assignedShifts.length > 0,
      gaps: assignedShifts.length === 0 ? [{ start: '00:00', end: '23:59', durationMinutes: 1439 }] : []
    };
  }

  // Parse opening and closing times
  const parseTimeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatMinutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  if (!openingHours.opening_time || !openingHours.closing_time) {
    return { isCovered: false, gaps: [] };
  }

  const openingMinutes = parseTimeToMinutes(openingHours.opening_time);
  const closingMinutes = parseTimeToMinutes(openingHours.closing_time);

  // Handle overnight hours (e.g., 22:00 - 02:00)
  let requiredCoverageSegments: Array<{ start: number; end: number }>;

  if (openingMinutes < closingMinutes) {
    // Normal hours within same day
    requiredCoverageSegments = [{ start: openingMinutes, end: closingMinutes }];
  } else {
    // Overnight hours - need coverage from opening to midnight AND from midnight to closing
    requiredCoverageSegments = [
      { start: openingMinutes, end: 24 * 60 }, // Late night portion
      { start: 0, end: closingMinutes } // Early morning portion
    ];
  }

  // Convert assigned shifts to coverage segments
  const coverageSegments: Array<{ start: number; end: number }> = [];

  assignedShifts.forEach(shift => {
    const shiftStart = new Date(shift.start_time);
    const shiftEnd = new Date(shift.end_time);

    const startMinutes = shiftStart.getHours() * 60 + shiftStart.getMinutes();
    const endMinutes = shiftEnd.getHours() * 60 + shiftEnd.getMinutes();

    coverageSegments.push({ start: startMinutes, end: endMinutes });
  });

  // Sort coverage segments by start time
  coverageSegments.sort((a, b) => a.start - b.start);

  // Find gaps in coverage
  const gaps: Array<{ start: string; end: string; durationMinutes: number }> = [];

  for (const requiredSegment of requiredCoverageSegments) {
    let currentPos = requiredSegment.start;

    for (const coverageSegment of coverageSegments) {
      // Only consider coverage segments that overlap with required segment
      const segmentStart = Math.max(coverageSegment.start, requiredSegment.start);
      const segmentEnd = Math.min(coverageSegment.end, requiredSegment.end);

      if (segmentStart < segmentEnd) {
        // There's overlap with required coverage
        if (currentPos < segmentStart) {
          // Gap before this coverage segment
          gaps.push({
            start: formatMinutesToTime(currentPos),
            end: formatMinutesToTime(segmentStart),
            durationMinutes: segmentStart - currentPos
          });
        }
        currentPos = Math.max(currentPos, segmentEnd);
      }
    }

    // Check for gap at the end of required segment
    if (currentPos < requiredSegment.end) {
      gaps.push({
        start: formatMinutesToTime(currentPos),
        end: formatMinutesToTime(requiredSegment.end),
        durationMinutes: requiredSegment.end - currentPos
      });
    }
  }

  return {
    isCovered: gaps.length === 0,
    gaps
  };
};

/**
 * Custom closed date options for validation
 */
export interface CustomClosedDateOptions {
  isCustomClosed?: boolean;
  closureReason?: string | null;
  closureType?: ClosureType;
}

/**
 * Validate a single day's schedule status with opening hours coverage check
 *
 * CRITICAL: This function is used by both ScheduleOverviewCard and BaseShiftCalendar.
 * Both components MUST pass shifts data in exactly the same ShiftData format:
 * - id: string
 * - employee_id: string | null (snake_case, not employeeId)
 * - start_time: string (ISO string, not Date object)
 * - end_time: string (ISO string, not Date object)
 */
export const validateDaySchedule = (
  date: Date,
  shifts: ShiftData[],
  openingHours?: OpeningHoursData | null,
  customClosedOptions?: CustomClosedDateOptions
): DayValidationResult => {
  const hasShifts = shifts.length > 0;
  const assignedShifts = shifts.filter(shift => shift.employee_id !== null);
  const allShiftsAssigned = hasShifts && assignedShifts.length === shifts.length;

  let status: ValidationStatus;
  let validationMessage: string;
  let openingHoursCovered = true;
  let coverageGaps: Array<{ start: string; end: string; durationMinutes: number }> = [];
  let closureType: ClosureType | null = null;

  // Check if day is closed due to custom closed date (highest precedence)
  const isCustomClosed = customClosedOptions?.isCustomClosed ?? false;

  // Check opening hours coverage if opening hours data is provided
  if (openingHours && assignedShifts.length > 0) {
    const coverage = checkOpeningHoursCoverage(assignedShifts, openingHours);
    openingHoursCovered = coverage.isCovered;
    coverageGaps = coverage.gaps;
  }

  // Enhanced validation logic with opening hours coverage and custom closed dates
  if (isCustomClosed) {
    // Custom closed date takes precedence
    status = 'closed';
    closureType = customClosedOptions?.closureType || 'custom_closed_date';
    validationMessage = customClosedOptions?.closureReason || 'Business closed (custom)';
  } else if (!hasShifts) {
    // Check if business is closed all day - if so, return "closed" status
    if (openingHours && openingHours.is_closed_all_day) {
      status = 'closed';
      closureType = 'weekly_schedule';
      validationMessage = 'Business closed all day';
    } else {
      // Day with no shifts planned
      status = 'red';
      validationMessage = 'No shifts scheduled';
    }
  } else if (allShiftsAssigned && openingHoursCovered) {
    // All shifts assigned AND opening hours fully covered
    status = 'green';
    validationMessage = 'Fully staffed and covered';
  } else if (allShiftsAssigned && !openingHoursCovered) {
    // All shifts assigned but gaps in opening hours coverage
    status = 'orange';
    const gapCount = coverageGaps.length;
    const totalGapMinutes = coverageGaps.reduce((sum, gap) => sum + gap.durationMinutes, 0);
    validationMessage = `Staffed but ${gapCount} coverage gap${gapCount > 1 ? 's' : ''} (${Math.round(totalGapMinutes / 60 * 10) / 10}h)`;
  } else {
    // Some shifts unassigned (regardless of coverage)
    status = 'orange';
    const unassignedCount = shifts.length - assignedShifts.length;
    validationMessage = `${unassignedCount} shift${unassignedCount > 1 ? 's' : ''} unassigned`;
  }

  return {
    status,
    hasShifts,
    allShiftsAssigned,
    shiftsCount: shifts.length,
    validationMessage,
    openingHoursCovered,
    coverageGaps,
    closureType
  };
};

/**
 * Validate a period (week/month) schedule status - simplified
 */
export const validatePeriodSchedule = (
  dayValidations: DayValidationResult[]
): { hasShifts: boolean; allAssigned: boolean; shiftsCount: number } => {
  const totalShifts = dayValidations.reduce((sum, day) => sum + day.shiftsCount, 0);
  const hasShifts = totalShifts > 0;
  
  // Period is "allAssigned" (green) only if ALL days with shifts have green status
  const allAssigned = hasShifts && dayValidations.every(day => {
    // Days without shifts don't affect the validation
    // Only days with shifts need to be fully assigned
    return !day.hasShifts || day.status === 'green';
  });
  
  return {
    hasShifts,
    allAssigned,
    shiftsCount: totalShifts
  };
};